import { useSelector } from "react-redux";
import store from "redux/store";

import {
    AppsContainerGroup,
    AppsContainerItem,
    HandleOpenModalType,
} from "types/apps-container";
import { StateRedux } from "types/generic";

import { THEME } from "enums/global.enum";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/pro-light-svg-icons";

import ApplicationItem from "./applicationitem";
import GoAiguaButton from "./goaiguabutton";

import { applicationLogos, logos, logosLight } from "utils/icons";
import { getIconFromLocalOrRemoteOrigin } from "utils/components/application-group-functions";
import { useTranslation } from "utils/hooks/use-translation.hook";

type ApplicationGroupProps = {
    data: AppsContainerGroup;
    canBeEdited: boolean;
    handleOpenModal: HandleOpenModalType;
};

const ApplicationGroup: React.FC<ApplicationGroupProps> = ({
    data,
    canBeEdited,
    handleOpenModal,
}) => {
    const { logo, items, colour, title } = data;
    const currentTheme = store.getState().app.theme;

    const translate = useTranslation();

    const sortItemsCallback = (
        prev: AppsContainerItem,
        current: AppsContainerItem
    ) => {
        return prev.itemOrder - current.itemOrder;
    };

    const handleEditButtonClick = () => {
        handleOpenModal({
            content: "edit",
            data: data.items,
            itemToEditName: data.title,
            containerId: data.id,
            editMode: "subgroup",
        });
    };

    const handleCreateNewAppClick = () => {
        handleOpenModal({
            content: "edit",
            data: data.items,
            itemToEditName: data.title,
            containerId: data.id,
            editMode: "app",
        });
    };

    const RenderItems = () => {
        let itemElements: JSX.Element[] = [];
        let available = false;

        const useRedux = useSelector((state: StateRedux) => state);

        items.sort(sortItemsCallback).forEach((item, index: number) => {
            available = false;

            if (item.visible) {
                useRedux.keycloak.userRoles.forEach((rol: string) => {
                    if (
                        (item.allowed &&
                            item.visible &&
                            item.roles.includes(rol)) ||
                        !item.roles.length
                    ) {
                        available = true;
                    }
                });

                const icon = getIconFromLocalOrRemoteOrigin({
                    iconURL: item.icon,
                    localIconList: applicationLogos,
                    iconSize: { width: "26px", height: "26px" },
                    fallback: applicationLogos["goaigua_dmd_icon"](),
                });

                itemElements.push(
                    <ApplicationItem
                        icon={icon}
                        title={item.title}
                        className={"ml-2xs"}
                        url={item.url || ""}
                        color={colour}
                        key={index}
                        available={available}
                        allowed={item.allowed}
                    />
                );
            }
        });
        return itemElements;
    };

    const groupIcon = getIconFromLocalOrRemoteOrigin({
        iconURL: logo,
        localIconList: currentTheme === THEME.DARK ? logos : logosLight,
    });

    return (
        <section className="c-application-group">
            <header className="c-application-group__header">
                {logo ? (
                    <div className="c-application-group__logo">{groupIcon}</div>
                ) : (
                    <h3 className="c-application-group__title">{title}</h3>
                )}

                <div className="c-application-group__edit-icon-container">
                    {canBeEdited && (
                        <button
                            className="c-application-group__edit-button"
                            onClick={handleEditButtonClick}
                        >
                            <FontAwesomeIcon icon={faEdit} />
                        </button>
                    )}
                </div>
            </header>

            <div className="c-application-group__list">
                {RenderItems()}

                {canBeEdited && !data.items.length ? (
                    <GoAiguaButton
                        text={translate("app.welcome.page.add_app")}
                        className="goaigua-button-outline c-application-group__add-button"
                        onClick={handleCreateNewAppClick}
                    />
                ) : null}
            </div>
        </section>
    );
};

export default ApplicationGroup;
